'use client'

import { CheckCircle2, AlertTriangle, Lightbulb, XCircle } from 'lucide-react'
import RichText from '@/components/RichText'
import Diagram from '@/components/lesson/Diagram'
import { parseRichExplanation } from '@/lib/render/rich-explanation'
import { structureExplanation } from '@/lib/render/explanation-structure'
import { parseTutorDiagramSpec } from '@/lib/tutor/tutor-diagram'
import { unescapeDisplayText } from '@/lib/render/display-escapes'

/** A→0, B→1, … Returns -1 for anything that isn't a single A–Z letter. */
function letterIndex(letter: string): number {
  const code = letter.trim().toUpperCase().charCodeAt(0) - 65
  return code >= 0 && code < 26 ? code : -1
}

/** The choice text for a letter, when the choices array is available. */
function choiceTextFor(letter: string, choices?: string[]): string | null {
  if (!choices) return null
  const i = letterIndex(letter)
  return i >= 0 && i < choices.length ? choices[i] : null
}

/**
 * Renders a question explanation. When a structured `rich` explanation is
 * present and valid, renders the graphical, sectioned view (idea → diagram →
 * steps → answer → why-the-other-choices-are-wrong → common slip). Otherwise
 * falls back to the plain `explanation` text (which RichText renders with line
 * breaks).
 *
 * The "why the other choices are wrong" section preserves — and elevates — the
 * legacy explanation's habit of naming the tempting wrong choice and ruling it
 * out. When the surface tells us which choice the student actually picked (and
 * that they were wrong), that choice's rationale is hoisted to the top with a
 * "You chose …" treatment.
 *
 * FIXED-LIGHT "exam paper" — pinned light tints + dark text, NO theme tokens or
 * `dark:` variants. It always renders inside a fixed-light answer surface
 * (review / practice / checkpoint / drill / results panels), so following the
 * app theme would flip the boxless step list to white-on-white in dark mode —
 * the "explanations invisible in dark mode" bug. Callers on a theme-FOLLOWING
 * surface (e.g. the admin preview) put it on a light/force-light wrapper so the
 * boxless steps have a light backdrop. Same principle as the diagram, which
 * already sits on a `force-light` white surface.
 */
export default function ExplanationView({
  rich,
  explanation,
  className,
  studentPick,
  isCorrect,
  correctChoice,
  choices,
}: {
  rich?: unknown
  explanation?: string | null
  className?: string
  /** Letter (MCQ) or value (SPR) the student picked; null if skipped/unknown. */
  studentPick?: string | null
  /** Whether the student's answer was correct; null/undefined if unknown. */
  isCorrect?: boolean | null
  /** The correct choice letter, when known. Used by the free-text fallback so
   *  the correct choice is never mislabeled as a ruled-out distractor (works
   *  even when the student answered incorrectly). */
  correctChoice?: string | null
  /** Choice texts in display order (A, B, C, …), for quoting the picked choice. */
  choices?: string[]
}) {
  const parsed = parseRichExplanation(rich)

  if (!parsed) {
    if (!explanation) return null
    // No structured `rich` explanation — lift the plain-text one into a
    // readable shape (lead + per-choice bullets, or numbered steps) instead of
    // a dense paragraph. Falls back to a plain paragraph when the text has no
    // clear structure, so we never make an explanation worse.
    return (
      <FreeTextExplanation
        text={explanation}
        className={className}
        studentPick={studentPick}
        isCorrect={isCorrect}
        correctChoice={correctChoice}
        choices={choices}
      />
    )
  }

  const diagram = parsed.diagram ? parseTutorDiagramSpec(parsed.diagram) : null

  const distractors = parsed.distractors ?? []
  const pick = typeof studentPick === 'string' ? studentPick.trim().toUpperCase() : null
  // Hoist the rationale for the student's OWN wrong pick — only when we know
  // they were wrong and a matching note exists (never on correct / SPR).
  const pickedWrong =
    isCorrect === false && pick ? (distractors.find((d) => d.choice === pick) ?? null) : null
  const pickedText = pickedWrong ? choiceTextFor(pickedWrong.choice, choices) : null
  // Remaining distractors, in choice order (A, B, C, …) regardless of how the
  // generator emitted them.
  const otherDistractors = distractors
    .filter((d) => d !== pickedWrong)
    .sort((a, b) => letterIndex(a.choice) - letterIndex(b.choice))

  return (
    <div className={`space-y-3 ${className ?? ''}`}>
      <div className="rounded-md border-l-2 border-sky-400 bg-sky-50 px-3 py-2 text-sm leading-relaxed text-sky-950">
        <Lightbulb className="mr-1.5 inline h-3.5 w-3.5 -translate-y-px" aria-hidden />
        <span className="font-medium">The idea. </span>
        <RichText text={parsed.idea} />
      </div>

      {diagram?.ok && (
        <div className="force-light overflow-x-auto rounded-md border border-border bg-white p-3">
          <Diagram diagram={diagram.diagram} />
        </div>
      )}

      <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-slate-800 marker:text-slate-500">
        {parsed.steps.map((s, i) => (
          <li key={i}>
            <RichText text={s} />
          </li>
        ))}
      </ol>

      <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          <span className="font-medium">Answer {parsed.answer}</span>
          {parsed.answerNote ? (
            <>
              {' — '}
              <RichText text={parsed.answerNote} />
            </>
          ) : null}
        </span>
      </div>

      {distractors.length > 0 && (
        <div className="rounded-md border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-950">
          <p className="mb-1.5 flex items-center gap-1.5 font-medium">
            <XCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Why the other choices are wrong
          </p>

          {pickedWrong && (
            <p className="mb-2 rounded border border-rose-300 bg-rose-100 px-2 py-1.5 text-rose-900">
              <span className="font-semibold">
                You chose {pickedWrong.choice}
                {pickedText ? (
                  <>
                    {' ('}
                    <RichText text={pickedText} />
                    {')'}
                  </>
                ) : null}
                {'. '}
              </span>
              <RichText text={pickedWrong.why} />
            </p>
          )}

          {otherDistractors.length > 0 && (
            <ul className="space-y-1 pl-1">
              {otherDistractors.map((d) => (
                <li key={d.choice} className="flex gap-1.5">
                  <span className="font-semibold">{d.choice}.</span>
                  <span>
                    <RichText text={d.why} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {parsed.commonMistake && (
        <p className="flex items-start gap-1.5 text-xs leading-relaxed text-slate-500">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <span className="font-medium">Common slip: </span>
            <RichText text={parsed.commonMistake} />
          </span>
        </p>
      )}
    </div>
  )
}

/**
 * Renders a PLAIN-TEXT explanation in an easier-to-read shape, using the same
 * visual language as the rich view above so the two feel consistent:
 *   - a per-distractor breakdown → a "why this answer" lead + an amber
 *     "why the other choices are wrong" bulleted list (the picked wrong choice
 *     is hoisted, exactly like the rich path);
 *   - a multi-step (math) derivation → a numbered list;
 *   - anything else → a plain paragraph (unchanged from before).
 */
function FreeTextExplanation({
  text,
  className,
  studentPick,
  isCorrect,
  correctChoice,
  choices,
}: {
  text: string
  className?: string
  studentPick?: string | null
  isCorrect?: boolean | null
  correctChoice?: string | null
  choices?: string[]
}) {
  // Prefer the known correct letter; otherwise, a correct answer means the
  // student's own pick IS the correct choice. Either way the structurer never
  // mislabels the correct choice as a ruled-out distractor.
  //
  // Unescape BEFORE structuring: a stored literal "\n" (two characters) is
  // invisible to the step/bullet detection, which splits on real newlines —
  // RichText would fix the glyphs but the explanation would stay one block.
  const structured = structureExplanation(unescapeDisplayText(text), {
    correctChoice: correctChoice ?? (isCorrect ? studentPick : null),
  })

  if (structured.kind === 'plain') {
    return <RichText text={structured.text} className={className} />
  }

  if (structured.kind === 'steps') {
    return (
      <ol
        className={`list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-slate-800 marker:text-slate-500 ${className ?? ''}`}
      >
        {structured.steps.map((s, i) => (
          <li key={i}>
            <RichText text={s} />
          </li>
        ))}
      </ol>
    )
  }

  // distractors
  const pick = typeof studentPick === 'string' ? studentPick.trim().toUpperCase() : null
  const pickedWrong =
    isCorrect === false && pick
      ? (structured.distractors.find((d) => d.choice === pick) ?? null)
      : null
  const pickedText = pickedWrong ? choiceTextFor(pickedWrong.choice, choices) : null
  const others = structured.distractors.filter((d) => d !== pickedWrong)

  return (
    <div className={`space-y-3 ${className ?? ''}`}>
      <div className="rounded-md border-l-2 border-sky-400 bg-sky-50 px-3 py-2 text-sm leading-relaxed text-sky-950">
        <Lightbulb className="mr-1.5 inline h-3.5 w-3.5 -translate-y-px" aria-hidden />
        <span className="font-medium">Why this answer. </span>
        <RichText text={structured.lead} />
      </div>

      <div className="rounded-md border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-950">
        <p className="mb-1.5 flex items-center gap-1.5 font-medium">
          <XCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Why the other choices are wrong
        </p>

        {pickedWrong && (
          <p className="mb-2 rounded border border-rose-300 bg-rose-100 px-2 py-1.5 text-rose-900">
            <span className="font-semibold">
              You chose {pickedWrong.choice}
              {pickedText ? (
                <>
                  {' ('}
                  <RichText text={pickedText} />
                  {')'}
                </>
              ) : null}
              {'. '}
            </span>
            <RichText text={pickedWrong.why} />
          </p>
        )}

        {others.length > 0 && (
          <ul className="space-y-1 pl-1">
            {others.map((d) => (
              <li key={d.choice} className="flex gap-1.5">
                <span className="font-semibold">{d.choice}.</span>
                <span>
                  <RichText text={d.why} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
